import React from "react";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import RoleBasedLayout from "./Components/Layout/RoleBasedLayout";
import PostHogIdentify from "./Components/PostHogIdentify";
import SessionBootstrap from "./Components/SessionBootstrap";
import AccountSynopsis from "./Pages/AccountSynopsis";
import NotFound from "./Pages/NotFound";
import OnboardingWizard from "./Pages/OnboardingWizard";
import Ops from "./Pages/Ops";
import SponsorTipsRedirect, {
  EmailTenantRedirect,
} from "./Pages/SponsorTipsRedirect";
import accountTeamRoutes from "./Routes/AccountTeamRoutes";
import authRoutes from "./Routes/AuthRoutes";
import cLevelRoutes from "./Routes/CLevelRoutes";
import executiveSponsoreLeadRoutes from "./Routes/ExecutiveSponsoreLeadRoutes";
import executiveSponsoreRoutes from "./Routes/ExecutiveSponsoreRoutes";
import izbaAdminRoutes from "./Routes/IzbaAdminRoutes";
import venderAdminRoutes from "./Routes/VenderAdminRoutes";
import { hasAccessToken } from "./Services/SessionStore";
import { useAppSelector } from "./Store/Hooks";
import {
  ROLES,
  isRole,
  isAccountTeamRole,
  getDefaultRouteForRole,
} from "./Utils/Constants/RoleConstants";
import { STORAGE_KEYS, getFromLocalStorage } from "./Utils/Helper/LocalStorage";

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAppSelector(
    state => state.authentication
  );
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const isUserAuthenticated =
    isAuthenticated || hasAccessToken() || !!loggedInUser;

  // Presentation-only: role routing is not a security boundary; the API enforces JWT/roles.
  const userRole = isUserAuthenticated
    ? user?.user?.role || loggedInUser?.role
    : null;
  const tenantSlug = loggedInUser?.slug || user?.user?.tenant_slug || null;
  const isIzbaAdminUser = isRole(userRole, ROLES.IZBA_ADMIN);

  const asChildRoute = (path: string, element: React.ReactNode) => {
    const normalizedPath = path.replace(/^\//, "");
    if (!normalizedPath) {
      return <Route key={path} index element={element} />;
    }
    return <Route key={path} path={normalizedPath} element={element} />;
  };

  const renderProtectedRoutes = () => {
    const executiveOps =
      loggedInUser?.executiveOps || user?.user?.executive_ops || false;

    // Presentation-only route sets; server-side authorization is the security control.
    const getRoutesForRole = (role: string | null | undefined) => {
      if (!role) return null;
      if (isRole(role, ROLES.IZBA_ADMIN)) return izbaAdminRoutes;
      if (isRole(role, ROLES.VENDOR_ADMIN)) return venderAdminRoutes;
      if (isRole(role, ROLES.C_LEVEL)) return cLevelRoutes;
      if (isRole(role, ROLES.EXECUTIVE_SPONSOR)) {
        const routes = [...executiveSponsoreRoutes];
        if (executiveOps) {
          routes.push({ path: "/ops", element: <Ops />, label: "Ops" });
          routes.push({
            path: "/account-synopsis/:accountId",
            element: <AccountSynopsis />,
            label: "Account Synopsis",
          });
        }
        return routes;
      }
      if (isRole(role, ROLES.EXECUTIVE)) {
        const routes = [...executiveSponsoreRoutes];
        if (executiveOps) {
          routes.push({ path: "/ops", element: <Ops />, label: "Ops" });
          routes.push({
            path: "/account-synopsis/:accountId",
            element: <AccountSynopsis />,
            label: "Account Synopsis",
          });
        }
        return routes;
      }
      if (isRole(role, ROLES.EXECUTIVE_SPONSOR_LEAD))
        return executiveSponsoreLeadRoutes;
      if (isAccountTeamRole(role)) return accountTeamRoutes;
      return null;
    };

    const roleRoutes = getRoutesForRole(userRole);

    if (roleRoutes) {
      const roleRouteElements = roleRoutes.map(({ path, element }) =>
        asChildRoute(path, element)
      );

      // IZBA admin keeps root-based URLs. Other roles use /:orgSlug/*.
      if (isRole(userRole, ROLES.IZBA_ADMIN)) {
        return (
          <Route
            path="/"
            element={<RoleBasedLayout executiveOps={executiveOps} />}
          >
            {roleRouteElements}
          </Route>
        );
      }

      return (
        <Route
          path="/:orgSlug"
          element={<RoleBasedLayout executiveOps={executiveOps} />}
        >
          {roleRouteElements}
          {/* Legacy email deep links under /:orgSlug */}
          <Route
            path="accounts"
            element={
              <EmailTenantRedirect targetPath="/my-portfolio-accounts" />
            }
          />
          <Route
            path="assessments"
            element={<EmailTenantRedirect targetPath="/pending-assessments" />}
          />
          <Route
            path="exec-ops"
            element={
              <EmailTenantRedirect
                targetPath="/ops"
                normalizeOpsAssessmentTab
              />
            }
          />
        </Route>
      );
    }
  };

  const postLoginPath = !isUserAuthenticated
    ? "/login"
    : isIzbaAdminUser
      ? getDefaultRouteForRole(userRole)
      : tenantSlug
        ? `/${tenantSlug}${getDefaultRouteForRole(userRole)}`
        : "/login";

  return (
    <Routes>
      {/* Auth Routes */}
      {authRoutes.map(({ path, element }) => (
        <Route
          key={path}
          path={path}
          element={
            isUserAuthenticated ? (
              <Navigate to={postLoginPath} replace />
            ) : (
              element
            )
          }
        />
      ))}

      <Route path="/onboard" element={<OnboardingWizard />} />

      {/* Email-compat redirects (query preserved) */}
      <Route
        path="/accounts"
        element={<EmailTenantRedirect targetPath="/my-portfolio-accounts" />}
      />
      <Route
        path="/assessments"
        element={<EmailTenantRedirect targetPath="/pending-assessments" />}
      />
      <Route
        path="/exec-ops"
        element={
          <EmailTenantRedirect targetPath="/ops" normalizeOpsAssessmentTab />
        }
      />
      <Route path="/guidance-library" element={<SponsorTipsRedirect />} />
      <Route path="/guidance-library/:slug" element={<SponsorTipsRedirect />} />

      {/* Protected Routes - Separate layouts based on role */}
      <Route
        path="/"
        element={
          isUserAuthenticated ? (
            <Navigate to={postLoginPath} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      {renderProtectedRoutes()}

      {/* Fallback route */}
      <Route
        path="*"
        element={
          isUserAuthenticated ? <NotFound /> : <Navigate to="/login" replace />
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <SessionBootstrap>
      <BrowserRouter>
        <PostHogIdentify />
        <AppRoutes />
      </BrowserRouter>
    </SessionBootstrap>
  );
};

export default App;
